import { Link } from 'react-router-dom';
import { Printer, ArrowLeft, LayoutDashboard, Settings, ShoppingCart, Truck, Warehouse, FileText, Info, Lightbulb, Store, Users, Tag, Activity } from 'lucide-react';

/*
 * MANUALE D'USO OmniGlide — pagina web stampabile (fonte unica).
 * Per aggiornarlo in futuro basta modificare l'array SECTIONS qui sotto:
 *  - type 'p'     -> paragrafo
 *  - type 'sub'   -> sottotitolo
 *  - type 'steps' -> elenco numerato (passi)
 *  - type 'list'  -> elenco puntato
 *  - type 'tip'   -> riquadro consiglio
 *  - type 'note'  -> riquadro nota/attenzione
 * Il PDF si ottiene dal pulsante "Stampa / Salva come PDF" (stampa del browser).
 */

const BLUE = '#1B4E8E';
const TEAL = '#2E9BC9';

const MANUAL_VERSION = 'Versione 1.1 — Giugno 2026';

const SECTIONS = [
    {
        id: 'panoramica',
        icon: LayoutDashboard,
        title: '1. Panoramica delle funzionalità',
        blocks: [
            { type: 'p', text: `OmniGlide è la piattaforma che ti aiuta a gestire in modo semplice tutta la tua attività di distribuzione: dagli ordini dei clienti fino alla consegna con i tuoi autisti, passando per magazzino e fatturazione. Tutto in un unico posto, accessibile da computer, tablet e telefono.` },
            { type: 'sub', text: 'Cosa puoi fare con OmniGlide' },
            { type: 'list', items: [
                'Raccogliere e organizzare gli ordini dei clienti giorno per giorno',
                'Assegnare gli ordini agli autisti e stampare i fogli di consegna',
                'Tenere sotto controllo le giacenze di magazzino',
                'Collegare il tuo gestionale (Danea Easyfatt o Fatture in Cloud) per sincronizzare prodotti, prezzi e documenti',
                'Gestire il catalogo prodotti, i listini e i documenti (DDT, fatture, listini)',
                'Offrire ai tuoi clienti un portale dedicato dove ordinare in autonomia',
            ]},
            { type: 'sub', text: 'I due tipi di utente' },
            { type: 'list', items: [
                'Amministratore: sei tu e i tuoi collaboratori. Gestite ordini, autisti, magazzino, catalogo e impostazioni.',
                'Cliente: le aziende a cui vendi. Accedono a un portale riservato per fare ordini e consultare listini e documenti.',
            ]},
            { type: 'tip', text: `Il menu a sinistra è la tua bussola: da lì raggiungi Ordini, Autisti, Magazzino, Listini, Catalogo, Documenti, Clienti, Comunicazioni, Gestionale, Analisi Gestionale e Azienda.` },
        ],
    },
    {
        id: 'configurazione',
        icon: Settings,
        title: '2. Configurazione iniziale',
        blocks: [
            { type: 'p', text: `Al primo accesso una procedura guidata ("Avvio") ti accompagna passo dopo passo. Puoi completarla subito o riprenderla in qualsiasi momento. Ecco i passaggi.` },
            { type: 'img', src: '/manual/onboarding.png', caption: 'La procedura guidata di configurazione iniziale' },

            { type: 'sub', text: '2.1 Dati azienda' },
            { type: 'steps', items: [
                'Apri la sezione Azienda (o il passo "Dati azienda" nella procedura di Avvio).',
                'Inserisci nome, Partita IVA, indirizzo, città, CAP, telefono ed email.',
                'Scegli il colore del tuo brand e carica il tuo logo: appariranno nell\'app e nei documenti PDF.',
                'Premi "Salva dati azienda".',
            ]},
            { type: 'note', text: `Nome, logo e colore sono personali della tua azienda e compaiono nel menu tuo e dei clienti. OmniGlide resta il nome della piattaforma.` },

            { type: 'sub', text: '2.2 Connessione a Danea Easyfatt' },
            { type: 'p', text: `Se usi Danea Easyfatt, puoi collegarlo per far dialogare gestionale e app. Vai in Gestionale e scegli "Danea Easyfatt" come gestionale collegato.` },
            { type: 'steps', items: [
                'Nella sezione Gestionale seleziona la scheda "Danea Easyfatt".',
                'Inserisci un nome utente e una password a tua scelta: sono le stesse credenziali che imposterai anche dentro Danea.',
                'Salva la configurazione.',
                'In Danea Easyfatt vai su Strumenti → E-Commerce e inserisci le stesse credenziali e gli indirizzi che l\'app ti mostra.',
            ]},
            { type: 'tip', text: `Con questo collegamento Danea potrà scaricare automaticamente gli ordini raccolti nell'app e inviare all'app prodotti, prezzi e giacenze.` },

            { type: 'sub', text: '2.3 Import dei prodotti (catalogo)' },
            { type: 'steps', items: [
                'Nella procedura di Avvio, al passo "Importa il catalogo da Danea", copia l\'indirizzo (URL) mostrato.',
                'In Danea Easyfatt vai su Strumenti → E-Commerce → Aggiorna prodotti, incolla l\'indirizzo e premi "Invia".',
                'Codici articolo, nomi, unità di misura e prezzi vengono importati automaticamente.',
                'Torna nell\'app e premi "Aggiorna stato" per verificare quanti articoli sono presenti.',
            ]},
            { type: 'note', text: `Trovi e gestisci tutti gli articoli nella sezione Catalogo. Puoi impostare l'unità di misura predefinita, disattivare le unità che non usi o eliminare articoli.` },

            { type: 'sub', text: '2.4 Import dei clienti' },
            { type: 'steps', items: [
                'Esporta l\'anagrafica clienti da Danea o dal tuo gestionale in formato Excel/OpenOffice oppure Easyfatt-XML.',
                'Nella procedura di Avvio, al passo "Importa l\'anagrafica clienti", carica il file.',
                'I clienti vengono creati in blocco con credenziali temporanee da consegnare a ciascun cliente per l\'accesso al portale.',
            ]},
            { type: 'tip', text: `Puoi aggiungere o modificare i clienti anche in un secondo momento dalla sezione Clienti, uno per uno o importandone altri.` },
        ],
    },
    {
        id: 'ordini',
        icon: ShoppingCart,
        title: '3. Sezione Ordini (visualizza, modifica, elimina)',
        blocks: [
            { type: 'p', text: `La sezione Ordini è il registro giornaliero degli ordini. Qui trovi tutti gli ordini di una data e puoi VISUALIZZARLI, MODIFICARLI o ELIMINARLI. Attenzione: la preparazione della merce e il completamento (spunta "preparato") NON avvengono qui, ma nella sezione Autisti (vedi capitolo 4). In Ordini gestisci il contenuto e i dati dell'ordine; in Autisti si prepara fisicamente la merce per la consegna.` },
            { type: 'img', src: '/manual/ordini.png', caption: 'La schermata Ordini: elenco degli ordini della data selezionata' },
            { type: 'sub', text: '3.1 Cosa puoi fare nella sezione Ordini' },
            { type: 'list', items: [
                'Selezionare la data per vedere gli ordini di quel giorno.',
                'Visualizzare ogni ordine: cliente, indirizzo di consegna, articoli, quantità ed eventuali note.',
                'Modificare un ordine: aggiungere o togliere articoli, cambiare le quantità, cambiare l\'indirizzo di consegna e le note.',
                'Eliminare un ordine non più valido.',
                'Inserire manualmente un nuovo ordine per conto di un cliente (oltre a quelli che arrivano dal portale).',
                'Assegnare o cambiare l\'autista dell\'ordine (se non è già assegnato in automatico dall\'autista predefinito del cliente).',
                'Scaricare il PDF "Totale Merce Ordini" con la somma di tutta la merce ordinata nella giornata.',
            ]},
            { type: 'sub', text: '3.2 Da dove arrivano gli ordini' },
            { type: 'list', items: [
                'Dai clienti, tramite il portale (ordini inseriti in autonomia da loro).',
                'Da te, inserendoli manualmente dalla sezione Ordini.',
            ]},
            { type: 'note', text: `Ogni ordine mostra lo stato di avanzamento della preparazione, ma la spunta delle righe come "preparato" e il completamento dell'ordine si fanno nella sezione Autisti. In Ordini vedi lo stato e gestisci i dati; in Autisti prepari e completi.` },
            { type: 'tip', text: `Assegna a ogni cliente un autista predefinito (nella sua scheda in Clienti): così i suoi ordini finiranno automaticamente sull'autista giusto, senza doverli smistare a mano.` },
            { type: 'sub', text: '3.3 Modificare un ordine' },
            { type: 'steps', items: [
                'Apri l\'ordine dall\'elenco della data.',
                'Aggiungi articoli cercandoli nel catalogo, oppure modifica le quantità delle righe presenti.',
                'Se serve, aggiorna l\'indirizzo di consegna: se il cliente ha più indirizzi registrati, puoi sceglierlo da un menù a tendina.',
                'Salva: le modifiche sono subito visibili e verranno usate anche nei documenti di consegna.',
            ]},
        ],
    },
    {
        id: 'autisti',
        icon: Truck,
        title: '4. Autisti: preparazione e completamento degli ordini',
        blocks: [
            { type: 'p', text: `La sezione Autisti è dove si PREPARANO e si COMPLETANO gli ordini per la consegna. Qui gestisci gli autisti/mezzi, prepari la merce spuntando gli articoli e stampi i documenti di consegna.` },
            { type: 'img', src: '/manual/autisti.png', caption: 'La schermata Autisti: schede autista e documenti di consegna' },
            { type: 'sub', text: '4.1 Creare e organizzare gli autisti' },
            { type: 'steps', items: [
                'Premi "Nuovo Autista" e inserisci il Nome autista e, se vuoi, il Mezzo di trasporto/Targa. Il numero progressivo dell\'autista viene assegnato automaticamente.',
                'Nella scheda di ogni cliente (sezione Clienti) imposta l\'autista predefinito e l\'ordine di consegna (da 1 a 100): indica la posizione del cliente lungo il giro.',
                'Gli ordini dei clienti vengono raggruppati automaticamente per autista.',
                'Puoi trascinare gli ordini per riordinarli secondo il percorso di consegna.',
            ]},
            { type: 'sub', text: '4.2 Preparare e completare gli ordini (solo qui)' },
            { type: 'steps', items: [
                'Apri gli ordini di un autista dalla sua scheda.',
                'Man mano che prepari la merce, spunta ogni riga come "preparato".',
                'Quando TUTTI gli articoli di un ordine sono spuntati, l\'ordine risulta pronto/evaso.',
                'Quando tutti gli ordini dell\'autista sono pronti, la scheda mostra lo stato "Pronto".',
            ]},
            { type: 'note', text: `Il completamento (evasione) di un ordine dipende esclusivamente dalla spunta di tutte le sue righe come "preparato", operazione che si effettua qui in Autisti. Solo gli ordini evasi vengono poi considerati per l'invio dei documenti al gestionale e per lo scarico automatico del magazzino.` },
            { type: 'sub', text: '4.3 Documenti per la consegna' },
            { type: 'list', items: [
                '"Ordini PDF": il dettaglio degli ordini dell\'autista, pronto da stampare.',
                '"Totale Merce": il riepilogo delle quantità totali da caricare sul mezzo.',
                '"Ordini Word": la stessa distinta in formato modificabile.',
                '"Merce Extra": il documento della merce aggiuntiva.',
            ]},
            { type: 'sub', text: '4.4 Invio automatico via email' },
            { type: 'steps', items: [
                'Apri "Programma email" nella sezione Autisti.',
                'Attiva l\'invio automatico e imposta uno o più orari nell\'arco della giornata.',
                'Scegli se inviare gli ordini di "oggi" o di "domani" e i giorni della settimana attivi.',
                'Aggiungi uno o più destinatari email.',
            ]},
            { type: 'note', text: `Gli orari fanno riferimento all'ora italiana (Europe/Rome). Puoi comunque inviare le email manualmente in qualsiasi momento e consultare lo storico degli invii.` },
        ],
    },
    {
        id: 'magazzino',
        icon: Warehouse,
        title: '5. Magazzino e sincronizzazione giacenze',
        blocks: [
            { type: 'p', text: `La sezione Magazzino ti mostra le giacenze dei prodotti e ti aiuta a tenerle aggiornate senza doppio lavoro.` },
            { type: 'img', src: '/manual/magazzino.png', caption: 'La schermata Magazzino con le giacenze' },
            { type: 'sub', text: 'Come funziona' },
            { type: 'list', items: [
                'Le giacenze arrivano dal gestionale collegato (Danea Easyfatt o Fatture in Cloud) tramite la sincronizzazione: l\'app le mostra in sola lettura.',
                'Puoi attivare la sincronizzazione automatica per mantenere le quantità sempre allineate.',
                'Quando gli ordini di un autista vengono tutti spuntati come "preparato", la merce viene scalata automaticamente dal magazzino.',
                'In caso di differenze puoi fare una rettifica manuale della giacenza di un articolo.',
            ]},
            { type: 'sub', text: 'Rettifica manuale' },
            { type: 'steps', items: [
                'Apri la sezione Magazzino e cerca l\'articolo interessato.',
                'Inserisci la quantità corretta o la variazione da applicare.',
                'Conferma: la giacenza viene aggiornata subito.',
            ]},
            { type: 'tip', text: `La sincronizzazione automatica e lo scarico al momento della preparazione riducono gli errori e ti evitano di aggiornare i numeri a mano.` },
        ],
    },
    {
        id: 'fatturazione',
        icon: FileText,
        title: '6. Fatturazione e gestionale',
        blocks: [
            { type: 'p', text: `Dalla sezione Gestionale scegli il programma con cui gestire i documenti. Puoi avere un solo gestionale collegato per volta.` },
            { type: 'img', src: '/manual/gestionale.png', caption: 'La scelta del gestionale nella sezione Gestionale' },
            { type: 'sub', text: '6.1 Danea Easyfatt' },
            { type: 'p', text: `Con Danea è il gestionale a "venire a prendere" gli ordini dall'app (vedi capitolo 2.2). Gli ordini raccolti in OmniGlide vengono scaricati in Danea, dove emetti i tuoi documenti come di consueto.` },
            { type: 'sub', text: '6.2 Fatture in Cloud' },
            { type: 'p', text: `Con Fatture in Cloud è l'app a inviare i documenti al tuo account, tramite un collegamento sicuro. Ogni azienda inserisce le proprie credenziali.` },
            { type: 'steps', items: [
                'In Gestionale seleziona la scheda "Fatture in Cloud".',
                'Copia l\'indirizzo "Redirect URI" mostrato dall\'app e incollalo nell\'app che crei su developers.fattureincloud.it (Impostazioni → Sviluppatore → Nuova app, tipo OAuth 2.0).',
                'Inserisci nel form il Client ID e il Client Secret ottenuti da Fatture in Cloud.',
                'Scegli il tipo di documento da generare (Ordine, DDT o Fattura) e se l\'invio deve essere automatico.',
                'Premi "Collega account" e autorizza l\'accesso: poi seleziona l\'azienda di destinazione.',
            ]},
            { type: 'note', text: `Il Client Secret viene salvato in modo cifrato e non è mai visibile. Puoi scollegare l'account in qualsiasi momento; le credenziali restano salvate per una nuova autorizzazione.` },
            { type: 'tip', text: `Puoi inviare i documenti manualmente per ogni ordine oppure lasciare che partano in automatico. È previsto anche l'invio massivo degli ordini della giornata.` },
        ],
    },
    {
        id: 'portale',
        icon: Store,
        title: '7. Il portale clienti',
        blocks: [
            { type: 'p', text: `Ogni tuo cliente ha un accesso riservato al portale, dove può fare ordini in autonomia e consultare listini e documenti. È il modo più comodo per ricevere gli ordini già pronti nella tua area di gestione.` },
            { type: 'img', src: '/manual/portale-login.png', caption: 'La pagina di accesso al portale clienti' },
            { type: 'sub', text: '7.1 Come accede il cliente' },
            { type: 'steps', items: [
                'Consegna al cliente le credenziali (email/nome utente e password) generate al momento dell\'import o della creazione del cliente.',
                'Il cliente apre la pagina di accesso e inserisce le credenziali.',
                'Al primo accesso gli verrà chiesto di scegliere una nuova password personale.',
            ]},
            { type: 'note', text: `Le credenziali temporanee si creano automaticamente quando importi o aggiungi un cliente. Puoi rigenerarle in qualsiasi momento dalla sezione Clienti.` },

            { type: 'sub', text: '7.2 Fare un ordine' },
            { type: 'steps', items: [
                'Il cliente apre la sezione "Ordini" del portale.',
                'Sceglie i prodotti dal catalogo e indica le quantità.',
                'Conferma l\'ordine, indicando la data di consegna desiderata.',
                'L\'ordine arriva subito nella tua sezione Ordini, pronto per essere assegnato a un autista.',
            ]},
            { type: 'tip', text: `Gli ordini inviati dai clienti seguono le stesse regole dei tuoi: se il cliente ha un autista predefinito, l'ordine viene raggruppato automaticamente sul giro giusto.` },

            { type: 'sub', text: '7.3 Consultare listini e documenti' },
            { type: 'list', items: [
                '"Listini Prezzi": il cliente vede i prezzi a lui riservati, sempre aggiornati.',
                '"Fatture" e "DDT": può scaricare i propri documenti in PDF quando li pubblichi.',
                '"Statistiche": un riepilogo dei suoi ordini e acquisti nel tempo.',
                '"Comunicazioni": gli avvisi e i messaggi che invii ai clienti.',
            ]},
            { type: 'note', text: `Ogni cliente vede soltanto i propri dati: ordini, listini e documenti restano riservati e separati da quelli degli altri clienti.` },
        ],
    },
    {
        id: 'catalogo',
        icon: ShoppingCart,
        title: '8. Catalogo prodotti',
        blocks: [
            { type: 'p', text: `Nella sezione Catalogo trovi tutti gli articoli che vendi. Gli articoli si popolano automaticamente dalla sincronizzazione con il gestionale (Danea Easyfatt o Fatture in Cloud), ma puoi anche gestirli a mano.` },
            { type: 'sub', text: 'Cosa puoi fare' },
            { type: 'list', items: [
                'Consultare codice articolo (Cod. Easyfatt/FIC), nome, unità di misura e prezzo.',
                'Impostare l\'unità di misura predefinita e disattivare quelle che non usi.',
                'Nascondere un articolo ai clienti (resta a uso interno) oppure eliminarlo.',
                'Importare il catalogo da file Excel/ODS oppure aggiornarlo dal gestionale.',
                'Cercare rapidamente un articolo con la barra di ricerca.',
            ]},
            { type: 'note', text: `Il prezzo mostrato negli ordini inviati al gestionale segue prima il listino interno; se assente, viene usato il prezzo di vendita dell'articolo sincronizzato dal gestionale.` },
        ],
    },
    {
        id: 'clienti',
        icon: Users,
        title: '9. Clienti e credenziali di accesso',
        blocks: [
            { type: 'p', text: `Nella sezione Clienti gestisci l'anagrafica delle aziende a cui vendi e le loro credenziali di accesso al portale.` },
            { type: 'sub', text: '9.1 Anagrafica cliente' },
            { type: 'list', items: [
                'Dati anagrafici: nome, P.IVA, codice fiscale, indirizzo, città, CAP, telefono, email.',
                'Uno o più indirizzi di consegna (usati poi nel menù a tendina degli ordini).',
                'Autista predefinito e ordine di consegna lungo il giro.',
                'Listino prezzi assegnato, Codice cliente Easyfatt/FIC e tipo documento gestionale (Ordine, DDT o Fattura).',
            ]},
            { type: 'sub', text: '9.2 Credenziali di accesso' },
            { type: 'p', text: `Aprendo la scheda di un cliente (dettagli) vedi le sue credenziali: nome utente e password temporanea. Accanto a ciascuna c'è un pulsante "Copia" per copiarle con un clic e inviarle al cliente.` },
            { type: 'steps', items: [
                'Le credenziali temporanee vengono create automaticamente quando importi o aggiungi un cliente (import Excel, sincronizzazione dal gestionale o creazione manuale).',
                'Usa i pulsanti "Copia" per copiare nome utente e password temporanea.',
                'Se serve, premi "Rigenera credenziali" per generare una nuova password temporanea, subito visibile e pronta da inviare.',
                'Al primo accesso il cliente sceglierà una propria password personale: da quel momento la password temporanea non è più mostrata.',
            ]},
            { type: 'tip', text: `Puoi importare più clienti in blocco (pulsante "Importa da Easyfatt/FIC") e scaricare un file con le credenziali generate da consegnare a ciascun cliente.` },
        ],
    },
    {
        id: 'listini',
        icon: Tag,
        title: '10. Listini prezzi',
        blocks: [
            { type: 'p', text: `Nella sezione Listini gestisci i prezzi dei prodotti. Ogni cliente può avere un listino assegnato e vede solo i prezzi a lui riservati nel portale.` },
            { type: 'list', items: [
                'Consultare e modificare i prezzi per articolo di ciascun listino.',
                'Sincronizzare i prezzi dal gestionale (Danea Easyfatt o Fatture in Cloud).',
                'Se sul gestionale non è attiva la funzione listini, l\'app carica automaticamente come Listino 1 i prezzi di vendita presenti a catalogo.',
            ]},
            { type: 'note', text: `Dopo la prima importazione del catalogo, verifica che tutti i listini siano stati importati correttamente. Se ne mancano alcuni, crea prima tutti i tipi di listino previsti e poi ripeti la procedura di importazione dal gestionale di riferimento.` },
        ],
    },
    {
        id: 'analisi',
        icon: Activity,
        title: '11. Analisi Gestionale',
        blocks: [
            { type: 'p', text: `La sezione Analisi Gestionale è un cruscotto che riepiloga lo stato dell'integrazione con il gestionale (Danea Easyfatt e Fatture in Cloud) e delle sincronizzazioni.` },
            { type: 'img', src: '/manual/gestionale.png', caption: 'Il cruscotto Analisi Gestionale' },
            { type: 'list', items: [
                'Stato della connessione a Fatture in Cloud (azienda collegata, validità del token) e a Danea Easyfatt.',
                'Sincronizzazione automatica e invio automatico ordini: attivi o no, con relativi orari.',
                'Conteggi rapidi: articoli a catalogo, clienti, listini.',
                'Ordini in attesa, inviati e non ancora pronti.',
                'Elenco delle attività recenti (sincronizzazioni e invii) con esito.',
            ]},
            { type: 'tip', text: `Usa questo cruscotto per verificare a colpo d'occhio che tutto sia collegato e che le sincronizzazioni stiano andando a buon fine.` },
        ],
    },
];

function Block({ block }) {
    switch (block.type) {
        case 'p':
            return <p className="manual-p">{block.text}</p>;
        case 'sub':
            return <h3 className="manual-sub">{block.text}</h3>;
        case 'steps':
            return (
                <ol className="manual-steps">
                    {block.items.map((it, i) => <li key={i}>{it}</li>)}
                </ol>
            );
        case 'list':
            return (
                <ul className="manual-list">
                    {block.items.map((it, i) => <li key={i}>{it}</li>)}
                </ul>
            );
        case 'tip':
            return (
                <div className="manual-callout manual-tip">
                    <Lightbulb className="manual-callout-icon" />
                    <div><span className="manual-callout-label">Consiglio</span>{block.text}</div>
                </div>
            );
        case 'note':
            return (
                <div className="manual-callout manual-note">
                    <Info className="manual-callout-icon" />
                    <div><span className="manual-callout-label">Nota</span>{block.text}</div>
                </div>
            );
        case 'img':
            return (
                <figure className="manual-figure">
                    <img src={block.src} alt={block.caption || ''} className="manual-img" loading="lazy" />
                    {block.caption && <figcaption className="manual-caption">{block.caption}</figcaption>}
                </figure>
            );
        default:
            return null;
    }
}

export default function ManualPage() {
    return (
        <div className="manual-root">
            <style>{manualCss}</style>

            {/* Barra strumenti (nascosta in stampa) */}
            <div className="manual-toolbar no-print">
                <Link to="/admin" className="manual-back" data-testid="manual-back-link">
                    <ArrowLeft className="h-4 w-4" /> Torna all'app
                </Link>
                <button className="manual-print-btn" onClick={() => window.print()} data-testid="manual-print-btn">
                    <Printer className="h-4 w-4" /> Stampa / Salva come PDF
                </button>
            </div>

            <div className="manual-page">
                {/* Copertina / intestazione */}
                <header className="manual-header">
                    <img src="/omniglide-logo.png" alt="OmniGlide" className="manual-logo" />
                    <h1 className="manual-title">Manuale d'uso</h1>
                    <p className="manual-subtitle">Guida semplice all'utilizzo della piattaforma OmniGlide</p>
                    <span className="manual-version">{MANUAL_VERSION}</span>
                </header>

                {/* Indice */}
                <nav className="manual-toc no-print" aria-label="Indice">
                    <h2 className="manual-toc-title">Indice</h2>
                    <ol>
                        {SECTIONS.map((s) => (
                            <li key={s.id}><a href={`#${s.id}`}>{s.title}</a></li>
                        ))}
                    </ol>
                </nav>

                {/* Sezioni */}
                {SECTIONS.map((s) => {
                    const Icon = s.icon;
                    return (
                        <section key={s.id} id={s.id} className="manual-section">
                            <h2 className="manual-section-title">
                                <span className="manual-section-icon"><Icon className="h-5 w-5" /></span>
                                {s.title}
                            </h2>
                            {s.blocks.map((b, i) => <Block key={i} block={b} />)}
                        </section>
                    );
                })}

                <footer className="manual-footer">
                    <img src="/omniglide-icon.png" alt="" className="manual-footer-icon" />
                    <p>OmniGlide — Gestione fluida merci &amp; logistica · {MANUAL_VERSION}</p>
                    <p className="manual-footer-help">Per assistenza contatta il tuo referente OmniGlide.</p>
                </footer>
            </div>
        </div>
    );
}

const manualCss = `
.manual-root { background: #EEF2F6; min-height: 100vh; color: #0F172A; font-family: 'Work Sans', 'Inter', system-ui, sans-serif; }
.manual-toolbar { position: sticky; top: 0; z-index: 10; display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 20px; background: #fff; border-bottom: 1px solid #D9E1EA; }
.manual-back { display: inline-flex; align-items: center; gap: 6px; color: ${BLUE}; font-weight: 600; text-decoration: none; font-size: 14px; }
.manual-back:hover { text-decoration: underline; }
.manual-print-btn { display: inline-flex; align-items: center; gap: 8px; background: ${BLUE}; color: #fff; border: none; border-radius: 10px; padding: 10px 16px; font-weight: 600; font-size: 14px; cursor: pointer; transition: background-color .15s ease; }
.manual-print-btn:hover { background: #143A6B; }

.manual-page { max-width: 820px; margin: 24px auto; background: #fff; border: 1px solid #E2E8F0; border-radius: 14px; padding: 48px 56px; box-shadow: 0 1px 3px rgba(15,23,42,.06); }

.manual-header { text-align: center; padding-bottom: 28px; margin-bottom: 28px; border-bottom: 3px solid ${TEAL}; }
.manual-logo { height: 92px; object-fit: contain; margin: 0 auto 14px; }
.manual-title { font-size: 34px; font-weight: 800; color: ${BLUE}; margin: 0; letter-spacing: -0.5px; }
.manual-subtitle { color: #475569; margin: 8px 0 14px; font-size: 16px; }
.manual-version { display: inline-block; font-size: 12px; font-weight: 600; color: ${BLUE}; background: #EAF2FB; border: 1px solid #CFE0F2; border-radius: 999px; padding: 4px 12px; }

.manual-toc { background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 12px; padding: 18px 22px; margin-bottom: 34px; }
.manual-toc-title { font-size: 13px; text-transform: uppercase; letter-spacing: .08em; color: #64748B; margin: 0 0 10px; }
.manual-toc ol { margin: 0; padding-left: 18px; }
.manual-toc li { margin: 6px 0; }
.manual-toc a { color: ${BLUE}; text-decoration: none; font-weight: 500; }
.manual-toc a:hover { text-decoration: underline; }

.manual-section { margin-bottom: 34px; }
.manual-section-title { display: flex; align-items: center; gap: 12px; font-size: 22px; font-weight: 700; color: ${BLUE}; margin: 0 0 14px; padding-bottom: 8px; border-bottom: 1px solid #E9EEF4; }
.manual-section-icon { display: inline-flex; align-items: center; justify-content: center; width: 34px; height: 34px; border-radius: 9px; background: linear-gradient(135deg, ${BLUE}, ${TEAL}); color: #fff; flex-shrink: 0; }
.manual-sub { font-size: 16px; font-weight: 700; color: #0F2A4D; margin: 20px 0 8px; }
.manual-p { font-size: 15px; line-height: 1.7; color: #1F2937; margin: 0 0 12px; }
.manual-list, .manual-steps { margin: 0 0 12px; padding-left: 22px; }
.manual-list li, .manual-steps li { font-size: 15px; line-height: 1.65; color: #1F2937; margin: 5px 0; }
.manual-list li::marker { color: ${TEAL}; }
.manual-steps li::marker { color: ${BLUE}; font-weight: 700; }

.manual-callout { display: flex; gap: 12px; border-radius: 10px; padding: 12px 16px; margin: 14px 0; font-size: 14px; line-height: 1.6; }
.manual-callout-icon { width: 20px; height: 20px; flex-shrink: 0; margin-top: 2px; }
.manual-callout-label { display: block; font-weight: 700; margin-bottom: 2px; }
.manual-tip { background: #EAF7FB; border: 1px solid #BEE3F0; color: #0B4A63; }
.manual-tip .manual-callout-icon, .manual-tip .manual-callout-label { color: ${TEAL}; }
.manual-note { background: #EAF2FB; border: 1px solid #CFE0F2; color: #143A6B; }
.manual-note .manual-callout-icon, .manual-note .manual-callout-label { color: ${BLUE}; }

.manual-figure { margin: 16px 0 18px; }
.manual-img { width: 100%; border: 1px solid #D9E1EA; border-radius: 10px; display: block; box-shadow: 0 1px 4px rgba(15,23,42,.08); }
.manual-caption { font-size: 12.5px; color: #64748B; text-align: center; margin-top: 6px; font-style: italic; }

.manual-footer { margin-top: 40px; padding-top: 18px; border-top: 1px solid #E9EEF4; text-align: center; color: #64748B; font-size: 13px; }
.manual-footer-icon { height: 30px; width: 30px; object-fit: contain; margin: 0 auto 8px; display: block; }
.manual-footer-help { margin-top: 4px; }

@media print {
    .no-print { display: none !important; }
    .manual-root { background: #fff; }
    .manual-page { max-width: none; margin: 0; border: none; border-radius: 0; padding: 0; box-shadow: none; }
    .manual-section { break-inside: avoid; }
    .manual-section-title { break-after: avoid; }
    .manual-callout { break-inside: avoid; }
    .manual-figure { break-inside: avoid; }
    .manual-img { box-shadow: none; }
    a { color: #0F172A; text-decoration: none; }
    @page { margin: 16mm 15mm; }
}

@media (max-width: 640px) {
    .manual-page { padding: 28px 20px; margin: 12px; }
    .manual-title { font-size: 26px; }
    .manual-section-title { font-size: 19px; }
}
`;
